import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import MainLayout from '../../../layouts/MainLayout';
import '../styles/MCQPractice.css';
import AILightComponent from '../../../components/AILightComponent';
import SpeechInputComponent from '../../../components/SpeechInputComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrochip, faMicrophone } from '@fortawesome/free-solid-svg-icons';

const SATTest = () => {
    const authState = useSelector(state => state.authReducer);
    const [fetchData, { loading }] = useFetch();
    const navigate = useNavigate();
    const location = useLocation();

    const [setId, setSetId] = useState(location.state.setId);
    const [setTitle, setSetTitle] = useState(location.state.setTitle);
    const [seriesId, setSeriesId] = useState(location.state.seriesId);
    const [seriesTitle, setSeriesTitle] = useState(location.state.seriesTitle);
    const [examId, setExamId] = useState(location.state.examId);
    const [examTitle, setExamTitle] = useState(location.state.examTitle);
    const [examType, setExamType] = useState(location.state.examType);
    const [timeLimitInMin, setTimeLimitInMin] = useState(location.state.timeLimitInMin);
    const [isRepeatPractice, setIsRepeatPractice] = useState(location.state.isRepeatPractice);

    const [practiceTitle, setPracticeTitle] = useState("Think 5Whys - SAT Practice");
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState(Array(10).fill(''));
    const [submitted, setSubmitted] = useState(false);
    const [timer, setTimer] = useState(0);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);

    const [answerAllBeforeSubmitMsg, setAnswerAllBeforeSubmitMsg] = useState(null);
    const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);

    const [isCheckedAISupport, setIsCheckedAISupport] = useState(false);
    const [showMicrophoneAlert, setShowMicrophoneAlert] = useState(false);

    useEffect(() => {
        if (!authState.isLoggedIn) return;
        fetchQuestions();
    }, [authState]);

    useEffect(() => {
        setStartTime(new Date());
        const intervalId = setInterval(() => {
            setTimer(prevTimer => prevTimer + 1);
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    const fetchQuestions = useCallback(() => {
        if (!setId) return;
        const config = {
            url: `/questionmaster/${setId}`,
            method: "get",
            headers: { Authorization: authState.token }
        };
        fetchData(config, { showSuccessToast: false })
            .then(response => {
                if (response && Array.isArray(response.questionlist)) {
                    setQuestions(response.questionlist);
                } else {
                    console.error("Invalid response format: Assignment not found in response");
                }
            })
            .catch(error => {
                console.error("Error fetching Assignments:", error);
            });
    }, [fetchData, setId]);

    const handleInputChange = (index, value) => {
        const newAnswers = [...answers];
        newAnswers[index] = value;
        setAnswers(newAnswers);
    };

    const handleSubmit = () => {
        if (submitted) {
            return;
        }

        const isFilled = answers.every(answer => answer.trim() !== '');
        if (!isFilled) {
            setAnswerAllBeforeSubmitMsg("Complete your exam. Give all the answers before submitting it.");
            return;
        }

        setEndTime(new Date());
        setSubmitted(true);
    };

    const formatTime = (timeInSeconds) => {
        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds % 3600) / 60);
        const seconds = timeInSeconds % 60;

        return `${hours}Hr ${minutes}Min ${seconds}Sec`;
    };

    useEffect(() => {
        const allAnswered = answers.every(answer => answer.trim() !== '');
        setAllQuestionsAnswered(allAnswered);
    }, [answers]);


    const handleChange = (e, index) => {
        const newAnswers = [...answers];
        newAnswers[index] = e.target.value;
        setAnswers(newAnswers);  
    };


    return (
        <MainLayout>
            <div>
                <h3 className='text-lg mt-1 mx-8 border-b border-b-gray-300 text-white'>Learnings</h3>
            </div>
            <div className="practice-test-container">
                <h1 className="practice-test-heading text-yellow-300 mb-8" style={{ fontSize: '1.5rem' }}>Think5Whys Short Answer Practice</h1>
                <p className='text-white'>Welcome, {authState.user && authState.user.name ? authState.user.name : 'Guest'}</p>
                <p className='text-white'>Practice Set: {setTitle}</p>
                <p className='text-white'>Practice Series: {seriesTitle}</p>
                <p className='text-white'>Practice for Exam: {examTitle}-{examType}</p>
                <p className='text-white'>Your Test Begins at: {startTime && startTime.toLocaleString()}</p>
                <p className='text-yellow-300'>Time Limit: {timeLimitInMin} Minutes</p>
                <p className='text-white'>Time Taken: {formatTime(timer)}</p><br /><br />
                {questions.map((question, index) => (
                    <div>
                        {/* AI Support &  Voice Input*/}
                        <div className="flex flex-row justify-between items-center cursor-pointer">
                            <div className='w-6/12'>
                                <input 
                                    type="checkbox" 
                                    className="mr-2 cursor-pointer"
                                    checked={isCheckedAISupport} 
                                    onChange={() => setIsCheckedAISupport(!isCheckedAISupport)} 
                                />
                                <span className="text-lg font-medium text-gray-600">Gen AI Support</span>
                                <FontAwesomeIcon icon={faMicrochip} className="text-yellow-500 text-xl ml-3" />
                            </div>
                            <div className='w-6/12'>
                                <input 
                                    type="checkbox" 
                                    className="mr-2 cursor-pointer"
                                    checked={showMicrophoneAlert} 
                                    onChange={() => setShowMicrophoneAlert(!showMicrophoneAlert)} 
                                />
                                <span className="text-lg font-medium text-gray-600">Enable Voice Input</span>
                                <FontAwesomeIcon icon={faMicrophone} className="text-yellow-500 text-xl ml-3" />
                            </div>
                        </div>
                    
                        <div className="question-container mt-2 text-white" key={index}>
                            <p className="question">Question {index + 1}:</p>
                            <p className="question text-yellow-300">{question.question}</p>

                            {/* <textarea
                                type="answer"
                                name="answer"
                                id={`answer-${index}`}
                                value={answers[index] || ''}
                                placeholder="Enter your answer here..."
                                onChange={(e) => handleInputChange(index, e.target.value)}
                                className='text-black bg-slate-300 w-full'
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    marginTop: '4px',
                                    resize: 'none',
                                    height: '250px',
                                    width: '100%',
                                    maxWidth: '100%',
                                    fontSize: '14px'
                                }}
                                maxLength={1000}
                                required
                            /> */}
                            { showMicrophoneAlert ? (
                                <SpeechInputComponent
                                    label="for SAT Question"
                                    name="answer"
                                    value={answers[index]}
                                    onChange={(e) => handleChange(e, index)}
                                    placeholder="Click Mic and Speak or Talk for Voice Input..."
                                    maxLength="2000"
                                    />
                                ) : (
                                <textarea 
                                    type="answer" 
                                    name="answer" 
                                    id="answer" 
                                    value={answers[index]}
                                    placeholder={showMicrophoneAlert ? "Write here or Click Mic for Voice Input...":"Write here.."}
                                    onChange={(e) => handleChange(e, index)}
                                    maxLength="2000"
                                    style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    backgroundColor: '#f0f0f0', 
                                    color: '#000000',
                                    maxWidth: '100%', 
                                    maxHeight: '150px' 
                                    }}
                                />
                            )}
                            {/* <p className="question">Answer:<br /> {question.idealShortAnswer}</p> */}
                        </div>
                        {isCheckedAISupport && answers[index] !== '' && (
                            <div className="mb-1">
                                <AILightComponent 
                                    param1={"Short Answer Type Question " + answers[index]} 
                                    param2={" my question " + question.queston + 
                                            " for completeness, clarity & correction. My answer is " + 
                                            answers[index]
                                        } 
                                />
                            </div>
                        )}

                    </div>
                ))}
                {!submitted && (
                    <div className="text-red-600">
                        {answerAllBeforeSubmitMsg}
                    </div>
                )}
                <button
                    className="save-practice-button"
                    onClick={handleSubmit}
                    disabled={!allQuestionsAnswered || submitted}
                >
                    Submit
                </button>
                {submitted && (
                    <div className="submission-details text-white">
                        <p>{authState.user && authState.user.name ? authState.user.name : 'Guest'} Your Practice Test Is Over.</p>
                        <p>Start Time: {startTime && startTime.toLocaleString()}</p>
                        <p>End Time: {endTime && endTime.toLocaleString()}</p>
                        <p>Time Taken: {formatTime(timer)}</p>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default SATTest;
